import type { SerializedNote } from './types';

export function exportMIDI(notes: SerializedNote[]): void {
  if (notes.length === 0) return;

  function freqToMidi(freq: number): number {
    if (freq <= 0) return 60;
    const midi = Math.round(69 + 12 * Math.log2(freq / 440));
    return Math.max(0, Math.min(127, midi));
  }

  const ticksPerQuarter = 128;
  const bpm = 120;
  const msPerTick = (60000 / bpm) / ticksPerQuarter;

  const trackEvents: number[] = [];

  // Tempo meta event (120 bpm)
  trackEvents.push(0, 0xff, 0x51, 0x03, 0x07, 0xa1, 0x20);

  let lastTick = 0;
  notes.forEach((note, idx) => {
    if (note.frequency <= 0) return;
    const midiNote = freqToMidi(note.frequency);
    const velocity = Math.round(Math.max(30, Math.min(127, note.amplitude * 127)));

    let absStartTick = 0;
    if (idx > 0) {
      const timeDeltaMs = note.timestamp - notes[0].timestamp;
      absStartTick = Math.round(timeDeltaMs / msPerTick);
    }
    const durationMs = note.duration || 300;
    const durationTicks = Math.max(20, Math.round(durationMs / msPerTick));
    const absEndTick = absStartTick + durationTicks;

    const deltaOn = Math.max(0, absStartTick - lastTick);
    trackEvents.push(...writeVariableLength(deltaOn));
    trackEvents.push(0x90, midiNote, velocity);
    lastTick = absStartTick;

    const deltaOff = Math.max(0, absEndTick - lastTick);
    trackEvents.push(...writeVariableLength(deltaOff));
    trackEvents.push(0x80, midiNote, 0x00);
    lastTick = absEndTick;
  });

  trackEvents.push(0, 0xff, 0x2f, 0x00);

  const trackLength = trackEvents.length;
  const header = [
    0x4d, 0x54, 0x68, 0x64,
    0x00, 0x00, 0x00, 0x06,
    0x00, 0x00,
    0x00, 0x01,
    (ticksPerQuarter >> 8) & 0xff, ticksPerQuarter & 0xff
  ];

  const trackHeader = [
    0x4d, 0x54, 0x72, 0x6b,
    (trackLength >> 24) & 0xff,
    (trackLength >> 16) & 0xff,
    (trackLength >> 8) & 0xff,
    trackLength & 0xff
  ];

  const midiBytes = new Uint8Array([...header, ...trackHeader, ...trackEvents]);
  const blob = new Blob([midiBytes], { type: 'audio/midi' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'hum-canvas-melody.mid';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function writeVariableLength(value: number): number[] {
  let buffer = value & 0x7f;
  const bytes: number[] = [];
  while ((value >>= 7) > 0) {
    buffer <<= 8;
    buffer |= (value & 0x7f) | 0x80;
  }
  while (true) {
    bytes.push(buffer & 0xff);
    if (buffer & 0x80) {
      buffer >>= 8;
    } else {
      break;
    }
  }
  return bytes;
}
